import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import queryString from 'query-string';

import { ENDPOINTS } from '@/configs/endpoints';
import { TREE_ROOT_NODE_ID, TreeNodeFile, treeNodeFileSchema } from '@/entities/Tree';
import { notificationsStore } from '@/stores/global/RootStore';
import { ILocalStore } from '@/stores/interfaces/ILocalStore';
import { MetaStore } from '@/stores/local/shared/MetaStore';
import { TreeStore } from '@/stores/local/shared/TreeStore';
import call from '@/utils/api';

import {
  MAX_CONCURRENT_UPLOADS,
  MAX_UPLOAD_FILES,
  MAX_UPLOAD_FILE_SIZE_MB,
  isLessThanUploadLimit,
  runWithConcurrencyLimit,
} from './utils';

type UploadDocumentStoreParams = {
  getOrganizationId: () => number;
  treeStore: TreeStore;
};

export type FailedUploadFile = {
  file: File;
  message: string;
};

type UploadSingleFileResult = 'success' | 'error';

export class UploadDocumentStore implements ILocalStore {
  private readonly _getOrganizationId: () => number;
  private readonly _treeStore: TreeStore;

  readonly meta = new MetaStore();

  files: File[] = [];

  uploadTotal = 0;

  uploadCompleted = 0;

  failedFiles: FailedUploadFile[] = [];

  hasError = false;

  constructor({ getOrganizationId, treeStore }: UploadDocumentStoreParams) {
    this._getOrganizationId = getOrganizationId;
    this._treeStore = treeStore;

    makeObservable(this, {
      files: observable.ref,
      uploadTotal: observable,
      uploadCompleted: observable,
      failedFiles: observable.ref,
      hasError: observable,
      uploadProgressPercent: computed,
      setFiles: action.bound,
      addDocuments: action.bound,
      reset: action.bound,
    });
  }

  get uploadProgressPercent(): number {
    if (this.uploadTotal === 0) {
      return 0;
    }

    return (this.uploadCompleted / this.uploadTotal) * 100;
  }

  setFiles(files: File[] | null): void {
    if (!files || files.length === 0) {
      this.files = [];
      this.hasError = false;
      this.failedFiles = [];

      return;
    }

    const validFiles: File[] = [];
    const oversizedFileNames: string[] = [];

    files.forEach((file) => {
      if (!isLessThanUploadLimit(file.size)) {
        oversizedFileNames.push(file.name);

        return;
      }

      validFiles.push(file);
    });

    if (oversizedFileNames.length > 0) {
      const message =
        oversizedFileNames.length === 1
          ? `Файл «${oversizedFileNames[0]}» превышает лимит ${MAX_UPLOAD_FILE_SIZE_MB} MB`
          : `Превышен лимит ${MAX_UPLOAD_FILE_SIZE_MB} MB для файлов: ${oversizedFileNames.join(', ')}`;

      notificationsStore.showError(message);
    }

    const filesToKeep = validFiles.length > MAX_UPLOAD_FILES ? validFiles.slice(0, MAX_UPLOAD_FILES) : validFiles;

    if (validFiles.length > MAX_UPLOAD_FILES) {
      notificationsStore.showError(`Можно загрузить не более ${MAX_UPLOAD_FILES} файлов за раз`);
    }

    this.files = filesToKeep;
    this.hasError = oversizedFileNames.length > 0 && filesToKeep.length === 0;
    this.failedFiles = [];
  }

  addDocuments = async (folderId?: string, sourceClassId?: string | null): Promise<void> => {
    if (this.files.length === 0) {
      return;
    }

    if (!sourceClassId) {
      notificationsStore.showError('Выберите источник');

      return;
    }

    if (this.meta.isLoading) {
      return;
    }

    const filesToUpload = [...this.files];

    this.meta.setLoadedStartMeta();

    runInAction(() => {
      this.uploadTotal = filesToUpload.length;
      this.uploadCompleted = 0;
      this.failedFiles = [];
    });

    const uploadResults = await runWithConcurrencyLimit(filesToUpload, MAX_CONCURRENT_UPLOADS, async (file) => {
      const result = await this._uploadSingleFile(file, folderId, sourceClassId);

      runInAction(() => {
        this.uploadCompleted += 1;
      });

      return { file, ...result };
    });

    const failedUploads: FailedUploadFile[] = uploadResults
      .filter((result) => result.status === 'error')
      .map(({ file, message }) => ({ file, message }));

    const successCount = uploadResults.filter((result) => result.status === 'success').length;
    const totalCount = filesToUpload.length;

    runInAction(() => {
      if (successCount === totalCount) {
        notificationsStore.showSuccess(`Загружено документов: ${totalCount} из ${totalCount}`);
        this.files = [];
        this.failedFiles = [];
        this.uploadTotal = 0;
        this.uploadCompleted = 0;
        this.meta.setLoadedSuccessMeta();

        return;
      }

      this.failedFiles = failedUploads;

      if (successCount > 0) {
        notificationsStore.showWarning(`Загружено документов: ${successCount} из ${totalCount}`);
      } else {
        notificationsStore.showError('Не удалось загрузить документы');
      }

      this.files = failedUploads.map(({ file }) => file);
      this.uploadTotal = 0;
      this.uploadCompleted = 0;
      this.meta.setLoadedErrorMeta();
    });
  };

  reset = (): void => {
    this.files = [];
    this.hasError = false;
    this.failedFiles = [];
    this.uploadTotal = 0;
    this.uploadCompleted = 0;
  };

  destroy(): void {
    this.meta.destroy();
  }

  private async _uploadSingleFile(
    file: File,
    folderId: string | undefined,
    sourceClassId: string,
  ): Promise<{ status: UploadSingleFileResult; message: string }> {
    const organizationId = this._getOrganizationId();
    const sourceClassIdNumber = Number(sourceClassId);

    const response = await call<TreeNodeFile>({
      endpoint: ENDPOINTS.documents.add(String(organizationId)),
      method: 'POST',
      data: {
        name: file.name,
        file,
        folder_id: folderId,
      },
      config: {
        params: {
          name: file.name,
          folder_id: folderId,
          source_class_id: sourceClassIdNumber,
        },
        paramsSerializer: {
          serialize: (params) => queryString.stringify(params, { skipNull: true }),
        },
      },
      multipartFormData: true,
    });

    if (response.isError) {
      return {
        status: 'error',
        message: response.error.response?.data.message ?? 'Что-то пошло не так',
      };
    }

    const parseResult = treeNodeFileSchema.safeParse(response.data);

    if (!parseResult.success) {
      return {
        status: 'error',
        message: 'Не удалось загрузить документ',
      };
    }

    const parentId = folderId ?? TREE_ROOT_NODE_ID;

    this._treeStore.addNode(parseResult.data, parentId);

    return { status: 'success', message: '' };
  }
}
